import React, { useState, useEffect } from "react";
// Note: useGoogleLogin removed - using backend redirect OAuth flow instead
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    CircularProgress,
    Alert,
    Chip,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    ListItemSecondaryAction,
    Divider,
} from "@mui/material";
import {
    FileSpreadsheet,
    RefreshCw,
    LogOut,
    CheckCircle,
    AlertCircle,
    Download,
    Link as LinkIcon,
    Unlink,
} from "lucide-react";
import axiosInstance from "../../../../../services/axiosInstance";

const API_BASE = process.env.REACT_APP_JUSTTAWK_IND_BASE_API_URL;
const TOKEN_KEY = "google_sheets_access_token";
const LINKED_SHEET_KEY = "google_sheets_linked_sheet";

const GoogleSheetsContent = ({ onClose, onSyncComplete }) => {
    const [accessToken, setAccessToken] = useState(null);
    const [userSheets, setUserSheets] = useState([]);
    const [linkedSheet, setLinkedSheet] = useState(null);
    const [loading, setLoading] = useState(true);
    const [syncing, setSyncing] = useState(false);
    const [message, setMessage] = useState({ type: "", text: "" });
    const [serviceAccountEmail, setServiceAccountEmail] = useState(null);
    const [duplicateLeads, setDuplicateLeads] = useState([]); // Store duplicates for download

    const userId = localStorage.getItem("userId");

    // Check for saved token and load linked sheet from backend
    useEffect(() => {
        const initializeModal = async () => {
            const savedToken = localStorage.getItem(TOKEN_KEY);

            // Load service account email for sharing instructions
            try {
                const saResponse = await axiosInstance.get(`${API_BASE}/google-sheets/service-account`);
                if (saResponse.data.success && saResponse.data.data?.serviceAccountEmail) {
                    setServiceAccountEmail(saResponse.data.data.serviceAccountEmail);
                }
            } catch (error) {
                console.log("Could not load service account info");
            }

            // Always try to load linked sheet from backend (persists across sessions)
            try {
                const response = await axiosInstance.get(`${API_BASE}/google-sheets/status/${userId}`);
                if (response.data.success && response.data.data?.hasLinkedSheet) {
                    const sheetData = {
                        id: response.data.data.sheetId,
                        name: response.data.data.sheetName || "Linked Sheet",
                        url: response.data.data.sheetUrl,
                    };
                    setLinkedSheet(sheetData);
                    localStorage.setItem(LINKED_SHEET_KEY, JSON.stringify(sheetData));
                }
            } catch (error) {
                console.log("No linked sheet found in backend");
            }

            // Check if user is connected to Google (via backend OAuth)
            try {
                const statusRes = await axiosInstance.get(`${API_BASE}/google-sheets/status/${userId}`);
                if (statusRes.data.success && statusRes.data.data?.isConnected) {
                    setAccessToken("connected"); // Just a flag to show connected UI
                    // Load sheets from backend if no sheet is linked yet
                    if (!statusRes.data.data?.hasLinkedSheet) {
                        listSpreadsheets();
                    }
                }
            } catch (error) {
                console.log("Could not check connection status");
            }

            setLoading(false);
        };

        initializeModal();
    }, [userId]);

    // Backend OAuth Popup - opens Google auth in popup, modal stays open
    const handleConnectGoogle = async () => {
        try {
            // Get OAuth URL from backend
            const response = await axiosInstance.get(`${API_BASE}/google-sheets/auth-url/${userId}`);
            if (response.data.success && response.data.authUrl) {
                // Open in popup window instead of redirect
                const width = 500;
                const height = 600;
                const left = window.screenX + (window.outerWidth - width) / 2;
                const top = window.screenY + (window.outerHeight - height) / 2;

                const popup = window.open(
                    response.data.authUrl,
                    'google-oauth',
                    `width=${width},height=${height},left=${left},top=${top},scrollbars=yes`
                );

                // Poll to check when popup closes
                setMessage({ type: "info", text: "Complete Google login in the popup window..." });
                const checkPopup = setInterval(async () => {
                    if (!popup || popup.closed) {
                        clearInterval(checkPopup);
                        // Check if connection was successful
                        try {
                            const statusRes = await axiosInstance.get(`${API_BASE}/google-sheets/status/${userId}`);
                            if (statusRes.data.success && statusRes.data.data?.isConnected) {
                                setAccessToken("connected");
                                setMessage({ type: "success", text: "✅ Connected! Now loading your sheets..." });
                                // Load sheets immediately
                                listSpreadsheets();
                            } else {
                                setMessage({ type: "error", text: "Connection cancelled or failed. Try again." });
                            }
                        } catch (err) {
                            setMessage({ type: "error", text: "Could not verify connection. Please try again." });
                        }
                    }
                }, 500);
            } else {
                setMessage({ type: "error", text: "Failed to get OAuth URL" });
            }
        } catch (error) {
            console.error("Error getting auth URL:", error);
            setMessage({ type: "error", text: "Failed to connect. Please try again." });
        }
    };

    // Check if we returned from OAuth callback (success or error)
    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const googleConnected = urlParams.get('google_connected');

        if (googleConnected === 'success') {
            setMessage({ type: "success", text: "✅ Connected! Refresh token saved for permanent auto-sync." });
            // Clear the URL param
            window.history.replaceState({}, document.title, window.location.pathname);
            // Reload status to get updated token info
            fetchStatus();
        } else if (googleConnected === 'error') {
            setMessage({ type: "error", text: "Failed to connect. Please try again." });
            window.history.replaceState({}, document.title, window.location.pathname);
        }
    }, []);

    const fetchStatus = async () => {
        try {
            const response = await axiosInstance.get(`${API_BASE}/google-sheets/status/${userId}`);
            if (response.data.success && response.data.data?.isConnected) {
                // User is now connected via backend OAuth
                setMessage({ type: "success", text: "✅ Google connected! Now link a sheet." });
            }
        } catch (err) {
            console.log("Could not fetch status");
        }
    };

    // List all user's spreadsheets (via backend using stored tokens)
    const listSpreadsheets = async () => {
        setLoading(true);
        try {
            // Use backend endpoint which uses stored OAuth tokens
            const response = await axiosInstance.get(`${API_BASE}/google-sheets/list-sheets/${userId}`);

            if (response.data.success) {
                const sheets = response.data.data.sheets || [];
                console.log("✅ Spreadsheets loaded:", sheets.length);
                setUserSheets(sheets);
                if (sheets.length === 0) {
                    setMessage({ type: "info", text: "No spreadsheets found. Create a sheet in Google Sheets first." });
                }
            } else {
                throw new Error(response.data.message);
            }
        } catch (err) {
            console.error("❌ Error listing spreadsheets:", err);
            if (err.response?.status === 400) {
                setMessage({ type: "error", text: "Not connected to Google. Please connect first." });
            } else {
                setMessage({ type: "error", text: `Failed to load spreadsheets: ${err.message}` });
            }
        } finally {
            setLoading(false);
        }
    };

    // Link a sheet
    const handleLinkSheet = async (sheet) => {
        try {
            setLoading(true);
            const sheetData = {
                id: sheet.id,
                name: sheet.name,
                url: `https://docs.google.com/spreadsheets/d/${sheet.id}`,
            };

            // Save to backend (including access token for auto-sync)
            await axiosInstance.post(`${API_BASE}/google-sheets/link-sheet`, {
                userId,
                sheetId: sheet.id,
                sheetName: sheet.name,
                sheetUrl: sheetData.url,
                accessToken, // Send token for auto-sync scheduler
            });

            // Save locally
            localStorage.setItem(LINKED_SHEET_KEY, JSON.stringify(sheetData));
            setLinkedSheet(sheetData);
            setMessage({ type: "success", text: `"${sheet.name}" linked! Auto-sync is now active.` });
        } catch (err) {
            console.error("❌ Error linking sheet:", err);
            setMessage({ type: "error", text: "Failed to link sheet" });
        } finally {
            setLoading(false);
        }
    };

    // Manual sync (uses backend stored tokens)
    const handleSync = async () => {
        if (!linkedSheet) {
            setMessage({ type: "error", text: "Please link a sheet first" });
            return;
        }

        try {
            setSyncing(true);
            setMessage({ type: "", text: "" });
            setDuplicateLeads([]); // Reset previous duplicates

            // Use backend sync endpoint which uses stored OAuth tokens
            const response = await axiosInstance.post(`${API_BASE}/google-sheets/sync`, {
                userId,
            });

            if (response.data.success) {
                setMessage({
                    type: "success",
                    text: `Sync complete! Created: ${response.data.data.created}, Skipped: ${response.data.data.skipped}`,
                });
                setDuplicateLeads(response.data.data.duplicateLeads || []);
                if (onSyncComplete) onSyncComplete();
            } else {
                setMessage({ type: "error", text: response.data.message });
            }
        } catch (err) {
            console.error("❌ Sync error:", err);
            if (err.response?.status === 400 && err.response?.data?.message?.includes("connect")) {
                setMessage({ type: "error", text: "Please reconnect to Google first." });
            } else {
                setMessage({ type: "error", text: err.response?.data?.message || "Sync failed" });
            }
        } finally {
            setSyncing(false);
        }
    };

    // Unlink sheet (but stay connected)
    const handleUnlinkSheet = async () => {
        try {
            await axiosInstance.delete(`${API_BASE}/google-sheets/unlink-sheet/${userId}`);
            localStorage.removeItem(LINKED_SHEET_KEY);
            setLinkedSheet(null);
            setMessage({ type: "success", text: "Sheet unlinked" });
        } catch (err) {
            console.error("❌ Error unlinking sheet:", err);
            setMessage({ type: "error", text: "Failed to unlink sheet" });
        }
    };

    // Full disconnect (logout from Google)
    const handleDisconnect = async () => {
        try {
            // Clear tokens from backend database
            await axiosInstance.delete(`${API_BASE}/google-sheets/unlink/${userId}`);
        } catch (err) {
            console.log("Error clearing backend tokens:", err);
        }
        // Clear frontend state
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(LINKED_SHEET_KEY);
        setAccessToken(null);
        setUserSheets([]);
        setLinkedSheet(null);
        setMessage({ type: "success", text: "Disconnected from Google" });
    };

    // Download Duplicates
    const handleDownloadDuplicates = () => {
        const sheetDuplicates = duplicateLeads.filter(l => l.reason === "Duplicate in Sheet");
        if (sheetDuplicates.length === 0) return;

        const headers = ["Sheet Row", "Phone Number", "Duplicate Reason"];
        const rows = sheetDuplicates.map(lead => [
            lead.row || "N/A",
            lead.mobile,
            `"${lead.reason || ""}"`
        ]);

        const csvContent = [headers, ...rows].map(e => e.join(",")).join("\n");
        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.setAttribute("href", url);
        link.setAttribute("download", `Sheet_Duplicates_${new Date().toISOString().split('T')[0]}.csv`);
        link.style.visibility = "hidden";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    // Download Template
    const handleDownloadTemplate = () => {
        const csvContent = `Name,Email,Phone,Company,Location
John Doe,john.doe@example.com,+1234567890,Acme Inc,Dubai
Jane Smith,jane.smith@example.com,+0987654321,Tech Corp,Abu Dhabi
Bob Johnson,bob@company.com,+1122334455,StartupXYZ,Sharjah`;

        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.setAttribute("href", url);
        link.setAttribute("download", "google_sheets_template.csv");
        link.style.visibility = "hidden";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    // Loading state
    if (loading && !accessToken) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {/* Message */}
            {message.text && (
                <Alert
                    severity={message.type === "error" ? "error" : "success"}
                    onClose={() => setMessage({ type: "", text: "" })}
                    action={
                        message.type === "success" && duplicateLeads.some(l => l.reason === "Duplicate in Sheet") && (
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
                                <Button
                                    color="inherit"
                                    size="small"
                                    onClick={handleDownloadDuplicates}
                                    startIcon={<Download size={16} />}
                                    sx={{ textTransform: "none", fontWeight: 600 }}
                                >
                                    Download Sheet Duplicates ({duplicateLeads.filter(l => l.reason === "Duplicate in Sheet").length})
                                </Button>
                                <Typography variant="caption" sx={{ fontSize: '10px', opacity: 0.8 }}>
                                    Lists leads appearing multiple times in the sheet
                                </Typography>
                            </Box>
                        )
                    }
                >
                    {message.text}
                </Alert>
            )}

            {/* Not Connected State */}
            {!accessToken ? (
                <Box sx={{ textAlign: "center", py: 3 }}>
                    {linkedSheet ? (
                        // Has linked sheet but no token - need to reconnect
                        <>
                            <FileSpreadsheet size={48} color="#0F9D58" style={{ marginBottom: 16 }} />
                            <Typography variant="h6" gutterBottom>
                                Reconnect to Sync
                            </Typography>
                            <Box
                                sx={{
                                    p: 2,
                                    mb: 2,
                                    bgcolor: "#E3F2FD",
                                    borderRadius: 1,
                                    display: "inline-block",
                                }}
                            >
                                <Typography variant="body2">
                                    <strong>Linked Sheet:</strong> {linkedSheet.name}
                                </Typography>
                            </Box>
                            <Typography color="text.secondary" sx={{ mb: 3 }}>
                                Your sheet is still linked. Reconnect your Google account to sync leads.
                            </Typography>
                            <Button
                                variant="contained"
                                onClick={handleConnectGoogle}
                                sx={{
                                    bgcolor: "#4285F4",
                                    "&:hover": { bgcolor: "#3367D6" },
                                    textTransform: "none",
                                    px: 4,
                                    py: 1.5,
                                }}
                            >
                                🔗 Reconnect Google Account
                            </Button>
                        </>
                    ) : (
                        // No token and no linked sheet - first time setup
                        <>
                            <AlertCircle size={48} color="#FF9800" style={{ marginBottom: 16 }} />
                            <Typography variant="h6" gutterBottom>
                                Connect to Google Sheets
                            </Typography>
                            <Typography color="text.secondary" sx={{ mb: 3 }}>
                                Import leads directly from your Google Spreadsheets
                            </Typography>
                            <Button
                                variant="contained"
                                onClick={handleConnectGoogle}
                                sx={{
                                    bgcolor: "#4285F4",
                                    "&:hover": { bgcolor: "#3367D6" },
                                    textTransform: "none",
                                    px: 4,
                                    py: 1.5,
                                }}
                            >
                                🔗 Connect Google Account
                            </Button>
                        </>
                    )}
                </Box>
            ) : (
                <>
                    {/* Connected Status */}
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            p: 2,
                            bgcolor: "#E8F5E9",
                            borderRadius: 1,
                        }}
                    >
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <CheckCircle size={20} color="#4CAF50" />
                            <Typography>Connected to Google</Typography>
                        </Box>
                        <Button
                            size="small"
                            color="error"
                            onClick={handleDisconnect}
                            startIcon={<LogOut size={16} />}
                            sx={{ textTransform: "none" }}
                        >
                            Disconnect
                        </Button>
                    </Box>

                    {/* Linked Sheet Display */}
                    {linkedSheet && (
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                p: 2,
                                bgcolor: "#E3F2FD",
                                borderRadius: 1,
                            }}
                        >
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                                <FileSpreadsheet size={24} color="#0F9D58" />
                                <Box>
                                    <Typography variant="subtitle2" fontWeight={600}>
                                        {linkedSheet.name}
                                    </Typography>
                                    <Chip label="Linked" size="small" color="success" sx={{ mt: 0.5 }} />
                                </Box>
                            </Box>
                            <Box sx={{ display: "flex", gap: 1 }}>
                                <Button
                                    size="small"
                                    variant="contained"
                                    onClick={handleSync}
                                    disabled={syncing}
                                    startIcon={syncing ? <CircularProgress size={16} color="inherit" /> : <RefreshCw size={16} />}
                                    sx={{
                                        bgcolor: "#0F9D58",
                                        "&:hover": { bgcolor: "#0B8043" },
                                        textTransform: "none",
                                    }}
                                >
                                    {syncing ? "Syncing..." : "Sync Now"}
                                </Button>
                                <Button
                                    size="small"
                                    color="error"
                                    onClick={handleUnlinkSheet}
                                    startIcon={<Unlink size={16} />}
                                    sx={{ textTransform: "none" }}
                                >
                                    Unlink
                                </Button>
                            </Box>
                        </Box>
                    )}

                    {/* Service Account Sharing Instructions */}
                    {linkedSheet && serviceAccountEmail && (
                        <Box
                            sx={{
                                p: 2,
                                bgcolor: "#FFF3E0",
                                borderRadius: 1,
                                border: "1px solid #FFB74D",
                            }}
                        >
                            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1, color: "#E65100" }}>
                                ⚡ Enable Permanent Auto-Sync
                            </Typography>
                            <Typography variant="body2" sx={{ mb: 1 }}>
                                Share your Google Sheet with this email for auto-sync to work forever (even when logged out):
                            </Typography>
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                    p: 1,
                                    bgcolor: "#FFF",
                                    borderRadius: 1,
                                    border: "1px solid #E0E0E0",
                                }}
                            >
                                <Typography
                                    variant="body2"
                                    sx={{
                                        flex: 1,
                                        fontFamily: "monospace",
                                        fontSize: "0.75rem",
                                        wordBreak: "break-all",
                                    }}
                                >
                                    {serviceAccountEmail}
                                </Typography>
                                <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={() => {
                                        navigator.clipboard.writeText(serviceAccountEmail);
                                        setMessage({ type: "success", text: "Email copied! Share your sheet with this address." });
                                    }}
                                    sx={{ textTransform: "none", minWidth: "auto" }}
                                >
                                    Copy
                                </Button>
                            </Box>
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
                                In Google Sheets: Click Share → Add this email as Viewer
                            </Typography>
                        </Box>
                    )}

                    {/* Sheet List */}
                    {!linkedSheet && (
                        <Box>
                            <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                Select a spreadsheet to link:
                            </Typography>

                            {loading ? (
                                <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                                    <CircularProgress size={24} />
                                </Box>
                            ) : userSheets.length === 0 ? (
                                <Alert severity="info">
                                    No spreadsheets found. Create one in Google Drive first.
                                </Alert>
                            ) : (
                                <List
                                    sx={{
                                        maxHeight: 300,
                                        overflow: "auto",
                                        border: "1px solid #E0E0E0",
                                        borderRadius: 1,
                                    }}
                                >
                                    {userSheets.map((sheet, index) => (
                                        <React.Fragment key={sheet.id}>
                                            {index > 0 && <Divider />}
                                            <ListItem sx={{ py: 1.5 }}>
                                                <ListItemIcon sx={{ minWidth: 40 }}>
                                                    <FileSpreadsheet size={24} color="#0F9D58" />
                                                </ListItemIcon>
                                                <ListItemText
                                                    primary={sheet.name}
                                                    primaryTypographyProps={{
                                                        sx: {
                                                            overflow: "hidden",
                                                            textOverflow: "ellipsis",
                                                            whiteSpace: "nowrap",
                                                            maxWidth: 250,
                                                        },
                                                    }}
                                                />
                                                <ListItemSecondaryAction>
                                                    <Button
                                                        size="small"
                                                        variant="outlined"
                                                        onClick={() => handleLinkSheet(sheet)}
                                                        startIcon={<LinkIcon size={14} />}
                                                        sx={{ textTransform: "none" }}
                                                    >
                                                        Connect
                                                    </Button>
                                                </ListItemSecondaryAction>
                                            </ListItem>
                                        </React.Fragment>
                                    ))}
                                </List>
                            )}
                        </Box>
                    )}
                </>
            )}

            <Divider sx={{ my: 1 }} />

            {/* Template Download */}
            {!(message.type === "success" && duplicateLeads.some(l => l.reason === "Already exists in Database")) && (
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        p: 2,
                        bgcolor: "#F5F5F5",
                        borderRadius: 1,
                    }}
                >
                    <Typography variant="body2" color="text.secondary">
                        Need help formatting your sheet?
                    </Typography>
                    <Button
                        size="small"
                        onClick={handleDownloadTemplate}
                        startIcon={<Download size={16} />}
                        sx={{ textTransform: "none" }}
                    >
                        Download Template
                    </Button>
                </Box>
            )}
        </Box>
    );
};

// Main Modal Component
const GoogleSheetsModal = ({ open, onClose, onSyncComplete }) => {
    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle
                sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    bgcolor: "#F8F9FA",
                }}
            >
                <FileSpreadsheet size={24} color="#0F9D58" />
                <Typography variant="h6">Google Sheets Integration</Typography>
            </DialogTitle>
            <DialogContent sx={{ pt: 3 }}>
                <GoogleSheetsContent onClose={onClose} onSyncComplete={onSyncComplete} />
            </DialogContent>
            <DialogActions sx={{ p: 2 }}>
                <Button onClick={onClose} sx={{ textTransform: "none" }}>
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default GoogleSheetsModal;
